import { UtensilsCrossed, ShoppingBag, UserSearch, HardHat } from "lucide-react";

export const NICHES = [
  {
    slug: "hospitality",
    name: "Hospitality",
    short: "Cafés, restaurants, pubs & catering",
    icon: UtensilsCrossed,
    accent: "#0090D0",
    heroLine: "Accountants who understand the kitchen, the till and the tips.",
    intro:
      "Thin margins, high staff turnover and a VAT system that treats a hot pasty differently from a cold one — hospitality is one of the hardest sectors to keep on top of financially. We work with cafés, restaurants, pubs and caterers across Bristol and beyond, so we know where the money leaks and how to plug it.",
    painsTitle: "What we see trip hospitality owners up",
    pains: [
      "Getting VAT wrong on food and drink — eat-in vs takeaway, hot vs cold, and alcohol all follow different rules.",
      "Tips and service charges handled informally, when the law now requires 100% to be passed to staff under a written policy.",
      "Payroll for shift-based teams — minimum wage by age band, holiday pay for irregular hours, and auto-enrolment for casual staff.",
      "Not knowing gross margin until the year-end accounts land, by which point it's too late to fix pricing or portion sizes.",
    ],
    guides: [
      {
        title: "VAT on food & drink: the essentials",
        summary: "Why your till needs to know the difference between a sandwich and a toastie.",
        points: [
          "Most eat-in food and drink is standard-rated (20%) — there are exceptions.",
          "Takeaway hot food and hot drinks are standard-rated; most cold takeaway food is zero-rated — again, there are exceptions.",
          "Alcohol, confectionery, crisps, soft drinks and bottled water are always standard-rated.",
          "Set up separate till buttons / product groups so sales are captured at the right rate automatically.",
          "Delivery platform fees (Deliveroo, Uber Eats, Just Eat) are an expense — record gross sales, not the net payout.",
        ],
        teaser: [
          "Why the same sandwich can carry two different VAT rates",
          "The till set-up mistake that quietly overpays (or underpays) HMRC",
          "What Deliveroo and Uber Eats payouts hide from your sales figures",
        ],
        checklist: [
          "Walk your menu and tag every item eat-in / takeaway, hot / cold.",
          "Check your till or EPOS has a separate rate button or product group for each VAT treatment.",
          "Pull one week of delivery-platform statements and confirm sales are recorded gross, with fees as expenses.",
          "Diarise a quarterly spot-check of 20 random sales against the VAT rate charged.",
        ],
      },
      {
        title: "Tips, service charges & troncs",
        summary: "What changed under the Employment (Allocation of Tips) Act — and how to stay compliant.",
        points: [
          "Since 1 October 2024, 100% of tips and service charges must be passed to workers, with no deductions other than tax.",
          "You need a written tipping policy and must distribute tips by the end of the month after they were received.",
          "A properly run tronc (with an independent troncmaster) can mean no employer or employee National Insurance on tips.",
          "Keep a tipping record for three years — workers have the right to request it.",
          "Card tips go through payroll; cash tips kept by staff are reported by the staff member on their own tax.",
        ],
        teaser: [
          "What the 2024 tipping law now requires of every employer",
          "How a properly run tronc can cut the National Insurance bill on tips",
          "The one record you must keep for three years",
        ],
        checklist: [
          "Write (or review) your tipping policy and share it with every member of staff.",
          "Confirm tips are being distributed by the end of the month after they were received.",
          "Decide whether a tronc is right for you — and who the independent troncmaster would be.",
          "Set up a tipping record and keep it for at least three years.",
        ],
      },
      {
        title: "Payroll for shift-based teams",
        summary: "Minimum wage, holiday pay and pensions when your rota changes every week.",
        points: [
          "From 1 April 2026 the National Living Wage is £12.71 (21+), £10.85 (18–20), £8.00 (16–17 and apprentices).",
          "Holiday pay for irregular-hours staff accrues at 12.07% of hours worked — make sure your software calculates it.",
          "Casual staff can be postponed from auto-enrolment for up to three months, but must still be assessed.",
          "Uniform costs, till shortages and training fees must never push pay below minimum wage.",
          "Rota and payroll software (Deputy, Planday, BrightHR) that talks to Xero saves hours each month.",
        ],
        teaser: [
          "The April 2026 minimum wage bands — and the deductions that accidentally breach them",
          "How holiday pay should work when the rota changes every week",
          "What auto-enrolment really means for casual and seasonal staff",
        ],
        checklist: [
          "Check every team member's rate against the April 2026 minimum wage band for their age.",
          "Confirm holiday pay for irregular-hours staff is accruing at 12.07% in your payroll software.",
          "Review deductions (uniform, till shortages, training) so they never push pay below minimum wage.",
          "Check auto-enrolment assessment is happening for casual staff, even where you postpone.",
        ],
      },
      {
        title: "Know your margins every week",
        summary: "The handful of numbers that tell you whether the business is actually making money.",
        points: [
          "Target gross profit: roughly 65–70% on food and 70–75% on wet sales — track them separately.",
          "A weekly stock take and GP report catches waste, theft and supplier price creep before they hurt.",
          "Labour cost as a percentage of sales (aim for 25–35%) is the second lever — review it against your rota.",
          "Link your EPOS to Xero so daily takings, card fees and VAT post automatically.",
          "Monthly management accounts turn all of this into decisions, not just numbers.",
        ],
        teaser: [
          "The two gross profit figures every hospitality owner should know by heart",
          "Why a weekly stock take pays for itself",
          "The second lever — after margin — that decides whether you make money",
        ],
        checklist: [
          "Set a weekly stock-take day and stick to it.",
          "Split food and wet sales in your EPOS and accounts so you can see two gross profit figures.",
          "Add labour cost as a % of sales to your weekly report, next to the rota.",
          "Book a 30-minute monthly review of your management accounts — with us or on your own.",
        ],
      },
    ],
    relatedPosts: ["navigating-employer-responsibilities-in-2024-a-guide-for-small-businesses", "autumn-budget-2025-what-it-really-means-for-you-your-business"],
  },
  {
    slug: "online-sellers",
    name: "Online sellers",
    short: "Shopify, Etsy, TikTok Shop & subscriptions",
    icon: ShoppingBag,
    accent: "#00B0E0",
    heroLine: "From side hustle to serious brand — without the HMRC surprises.",
    intro:
      "Whether you sell on Shopify, Etsy, Amazon, TikTok Shop or run a subscription box, your money arrives as lumpy marketplace payouts with fees already taken off. We help e-commerce and subscription businesses see their real sales, stay on the right side of VAT, and know their numbers well enough to scale.",
    painsTitle: "What we see trip online sellers up",
    pains: [
      "Recording the payout as the sale — so revenue, fees and VAT are all understated and the accounts don't reflect reality.",
      "Sailing past the £90,000 VAT threshold without noticing, because it's measured on a rolling 12 months, not the tax year.",
      "Platforms now report seller data to HMRC — if what you declare doesn't match, questions follow.",
      "No handle on stock, cost of sales or ad spend return — so growth eats cash without anyone realising.",
    ],
    guides: [
      {
        title: "Marketplace payouts: recording sales properly",
        summary: "Why the amount that lands in your bank is not your turnover.",
        points: [
          "Record gross sales, then show platform fees, payment processing fees and refunds as separate expenses.",
          "Use a connector (A2X, Link My Books, Dext Commerce) to split each payout into sales, fees, VAT and refunds automatically in Xero.",
          "Many platform fees from overseas companies are 'reverse charge' services for VAT — your accountant needs to see them.",
          "Reconcile every payout to the platform's settlement report, not just to the bank.",
          "Keep each channel (Shopify, Etsy, Amazon…) as its own tracking category so you can see which is actually profitable.",
        ],
        teaser: [
          "Why the amount Shopify or Amazon pays you is not your turnover",
          "The tool that splits every payout into sales, fees, VAT and refunds automatically",
          "The overseas-fee VAT rule most sellers have never heard of",
        ],
        checklist: [
          "Pick one payout from last month and reconcile it line by line to the platform settlement report.",
          "Check whether a connector (A2X, Link My Books, Dext Commerce) is feeding Xero or QuickBooks.",
          "List every overseas platform fee and flag it for reverse-charge VAT treatment.",
          "Set up a tracking category for each sales channel.",
        ],
      },
      {
        title: "VAT for online sellers",
        summary: "Thresholds, marketplaces and selling abroad — the rules that catch people out.",
        points: [
          "You must register once taxable sales exceed £90,000 in any rolling 12-month period — check it monthly.",
          "Marketplaces are the 'deemed supplier' for VAT on many imported goods and overseas-seller sales — know when this applies to you.",
          "Many platform fees charged by overseas companies may be subject to the VAT reverse charge, so it's important that your accountant can see these transactions.",
          "Selling into the EU can change your VAT and customs obligations straight away — the rules depend on what you sell, who you sell to and where your customer is based.",
          "Zero-rated goods (children's clothes, most books, some food) still count towards the threshold.",
          "Voluntary registration can make sense if your customers are mostly VAT-registered businesses.",
        ],
        teaser: [
          "How the VAT threshold is really measured — it's not the tax year",
          "When a marketplace handles the VAT for you, and when it doesn't",
          "What changes the moment you sell into the EU",
        ],
        checklist: [
          "Add up taxable sales for the last 12 months — and set a monthly reminder to repeat it.",
          "Note which of your marketplaces act as deemed supplier, and for which sales.",
          "Selling abroad? Check you understand the rules for your business — and speak to an expert if you're unsure.",
          "Consider whether voluntary registration would help if most customers are VAT-registered businesses.",
        ],
      },
      {
        title: "Side hustle to real business",
        summary: "When to register, when to go limited, and what HMRC already knows.",
        points: [
          "£1,000 or less of gross trading income? The trading allowance usually means there's no tax to pay and you may not need to report it to HMRC, although you may still need to report it depending on your circumstances.",
          "Above that, register for Self Assessment by 5 October after the end of the tax year you started trading.",
          "Digital platforms may report details of your sales, transactions and payment information to HMRC each year — so assume HMRC can see your online selling activity.",
          "A limited company usually starts to pay off once profits pass roughly £40–50k, or when you want to protect a brand.",
          "Open a separate business bank account from day one — it makes everything that follows ten times easier.",
        ],
        teaser: [
          "When a hobby officially becomes a business in HMRC's eyes",
          "What online platforms already report about you to HMRC",
          "The profit level where a limited company starts to make sense",
        ],
        checklist: [
          "Work out your gross trading income this tax year against the £1,000 trading allowance.",
          "If you've passed it, register for Self Assessment before 5 October following the tax year.",
          "Open a dedicated business bank account.",
          "Keep a simple monthly profit figure so you'll know when a limited company starts to make sense.",
        ],
      },
      {
        title: "Subscription businesses: the numbers that matter",
        summary: "Revenue recognition, churn and the metrics investors and lenders will ask for.",
        points: [
          "Money received up front for future months is deferred revenue, not profit — recognise it as you deliver.",
          "Track Monthly Recurring Revenue (MRR), churn rate and customer lifetime value alongside your P&L.",
          "Payment processor fees (Stripe, GoCardless, Recharge) belong in cost of sales, not 'bank charges'.",
          "Failed payments and dunning are a cashflow issue as much as a customer issue — monitor recovery rates.",
          "Monthly management accounts let you spot a rising cost-to-acquire before it becomes a problem.",
        ],
        teaser: [
          "Why money received up front isn't profit yet",
          "The three subscription metrics lenders and investors ask for first",
          "Where Stripe and GoCardless fees should really sit in your accounts",
        ],
        checklist: [
          "Check that upfront payments are being recognised over the months they cover.",
          "Set up MRR, churn and customer lifetime value as monthly KPIs alongside your P&L.",
          "Move processor fees (Stripe, GoCardless, Recharge) into cost of sales.",
          "Review your failed-payment recovery rate and dunning settings.",
        ],
      },
    ],
    relatedPosts: ["navigating-the-tax-maze-of-side-hustles-understanding-your-obligations", "understanding-the-spring-budget-2024-a-guide-for-our-clients"],
  },
  {
    slug: "recruitment",
    name: "Recruitment",
    short: "Agencies & independent consultants",
    icon: UserSearch,
    accent: "#103080",
    accentText: "#40C0E0",
    heroLine: "You place people. We keep the cash, compliance and margins in line.",
    intro:
      "Recruitment is a cashflow business dressed up as a people business — you pay temps weekly while clients pay in 30, 60 or 90 days, and the compliance landscape around umbrellas and off-payroll working keeps shifting. We work with agencies and independent consultants to keep the engine running and the margins visible.",
    painsTitle: "What we see trip recruiters up",
    pains: [
      "Temp desk cashflow — funding weekly payroll while waiting on client payment terms.",
      "Since 6 April 2026, the agency contracting with the end client can be jointly liable for PAYE tax and National Insurance due on workers supplied through an umbrella company.",
      "IR35 responsibilities depend on the end client and contractual chain — the end client usually determines status, while an agency may be responsible for operating PAYE. Different rules apply to small private-sector clients.",
      "Not knowing gross profit per consultant, per desk or per client — so the wrong activity gets rewarded.",
    ],
    guides: [
      {
        title: "Cashflow for temp & contract desks",
        summary: "How to fund weekly payroll without strangling the business.",
        points: [
          "Model the gap: weekly payroll and clients paying 45 days after invoicing can mean funding around seven weeks of payroll. Allow extra for invoicing delays, late payments, employer costs, tax payments and overheads.",
          "Invoice finance, such as factoring or confidential invoice discounting, can help bridge the payroll funding gap — compare total costs, funding limits, client concentration restrictions and your liability if a client does not pay.",
          "Tighten credit control: agree terms in writing, invoice weekly from approved timesheets where agreed, and contact clients before invoices fall due to resolve any payment issues.",
          "Watch client concentration — use 25% of revenue as a prompt to review your exposure, and check how much of your unpaid invoices relates to that client and whether your lender imposes any limits.",
          "A rolling 13-week cashflow forecast is a practical tool for spotting payroll funding gaps before they become a problem — update it weekly.",
        ],
        teaser: [
          "How to work out exactly how many weeks of payroll you need to fund",
          "What to compare before signing an invoice finance deal",
          "The single report every temp desk should run weekly",
        ],
        cta: "Know there's a gap between paying your temps and getting paid, but unsure how much funding you need? We can help you forecast the gap and plan ahead — start with a free, informal discovery call with us, in person at the Science Park or by video call.",
        checklist: [
          "Work out your payroll-to-payment gap in weeks and the working capital it ties up.",
          "Build (or refresh) a rolling 13-week cashflow forecast.",
          "Review credit terms with your top five clients and invoice weekly from approved timesheets where agreed.",
          "Use 25% of revenue as a prompt to review client concentration — check how much of your unpaid invoices relates to that client and whether your lender imposes any limits.",
        ],
      },
      {
        title: "Umbrella companies & agency liability",
        summary: "What changed from April 2026 and the checks every agency now needs to make.",
        points: [
          "Since 6 April 2026, the agency contracting with the end client is generally jointly and severally liable with the umbrella company for relevant PAYE tax and National Insurance.",
          "Build a preferred supplier list, verify any claimed independent accreditation (e.g. FCSA, Professional Passport) and carry out your own ongoing checks — accreditation alone does not guarantee compliance.",
          "Check payslips and pay breakdowns: workers must receive at least the applicable minimum wage, with clear deductions and holiday pay arrangements.",
          "Be wary of promises of unusually high take-home pay or tax-free employment payments — avoidance schemes can expose your agency to unpaid tax and other liabilities.",
          "Keep records of your checks — they help you identify and manage risk, but do not remove your liability for unpaid PAYE tax and National Insurance.",
        ],
        teaser: [
          "What changed for agencies on 6 April 2026 — and who's now liable",
          "How to vet an umbrella company before it goes on your list",
          "The warning signs that an umbrella may be running an avoidance scheme",
        ],
        checklist: [
          "Build a preferred supplier list of umbrella companies and verify any claimed accreditation yourself.",
          "Review sample worker payslips and obtain evidence of PAYE reporting and payments to HMRC.",
          "Document your due-diligence checks and where the records are kept.",
          "Schedule regular reviews, with additional checks when ownership, payment arrangements or other risk factors change.",
        ],
      },
      {
        title: "IR35 & off-payroll working",
        summary: "Who decides, who pays, and how to keep contractors compliant.",
        points: [
          "For public-sector clients and medium or large private-sector clients with a UK connection, the client issues a Status Determination Statement (SDS). For inside-IR35 engagements, the deemed employer — often the agency paying the contractor's company — operates PAYE and pays employer National Insurance.",
          "For small private-sector clients, including qualifying voluntary-sector organisations, the contractor's own company remains responsible for assessing IR35 and accounting for any tax due.",
          "Keep copies of SDSs and pass them to the next party in the supply chain where relevant — failing to pass on an SDS you receive can leave your agency responsible for PAYE and National Insurance.",
          "Use HMRC's CEST tool to help assess status, and save the answers, result and evidence of actual working practices. Review the assessment when arrangements change.",
          "A statement of work does not automatically put an engagement outside IR35 — the actual arrangements determine who has responsibility and whether the rules apply.",
        ],
        teaser: [
          "Who actually decides a contractor's status — and who pays if it's wrong",
          "The piece of paperwork that can leave your agency holding the PAYE bill",
          "Why a statement of work doesn't automatically take a contractor outside IR35",
        ],
        checklist: [
          "Check each client's size and who holds responsibility for the status determination.",
          "Obtain and retain SDSs where applicable, and confirm they reach the relevant parties.",
          "Record the reasoning and working practices behind each determination.",
          "Review statement-of-work arrangements against actual working practices and confirm who holds the client responsibilities.",
        ],
      },
      {
        title: "Margins & KPIs for recruiters",
        summary: "The numbers that separate a busy agency from a profitable one.",
        points: [
          "Track gross profit per consultant and desk, then compare it with employment costs and allocated overheads to understand profitability.",
          "Compare permanent fees as a percentage of the agreed salary basis and temporary gross margins by client — use consistent calculations and review low-margin work.",
          "Track time to fill, fill ratio and interview-to-placement conversion to help identify bottlenecks in your recruitment pipeline.",
          "Review rebate and clawback exposure on permanent fees, and reflect expected refunds appropriately in your accounts and cashflow forecast.",
          "Model commission against gross profit and check that deals still contribute to profit after commission, rebates and other relevant costs.",
        ],
        teaser: [
          "How to tell whether each consultant desk is actually profitable",
          "Three pipeline numbers that show where placements get stuck",
          "Why commission on revenue can quietly reward unprofitable deals",
        ],
        checklist: [
          "Calculate gross profit per consultant for the last quarter.",
          "Compare perm fee % and temp gross margin % by client — using consistent calculations — and shortlist low-margin work to review.",
          "Add time-to-fill and fill ratio to your monthly reporting.",
          "Review your commission scheme against gross profit, and check that expected rebates are reflected appropriately in your accounts and forecast.",
        ],
      },
    ],
    relatedPosts: ["navigating-employer-responsibilities-in-2024-a-guide-for-small-businesses", "autumn-budget-2025-what-it-really-means-for-you-your-business"],
  },
  {
    slug: "construction",
    name: "Construction & trades",
    short: "Contractors, subcontractors & CIS",
    icon: HardHat,
    accent: "#0090D0",
    heroLine: "CIS, reverse charge and retentions — handled, so you can build.",
    intro:
      "Construction has its own tax rulebook: the Construction Industry Scheme, VAT domestic reverse charge, retentions and stage payments. Get them wrong and HMRC penalties or cashflow problems follow quickly. We look after contractors, subcontractors and trades across the South West, from one-van operations to growing firms.",
    painsTitle: "What we see trip builders and trades up",
    pains: [
      "Missing the monthly CIS contractor return deadline — the 19th following the tax month — can trigger a £100 penalty from one day late, with further penalties for continued delay.",
      "Invoicing with VAT when the domestic reverse charge should apply — or vice versa.",
      "Retentions forgotten about and never collected, sometimes years after the job finished.",
      "Pricing jobs on turnover rather than knowing the real margin after materials, labour and wasted days.",
    ],
    guides: [
      {
        title: "The Construction Industry Scheme (CIS) explained",
        summary: "Your responsibilities as a contractor, and what to expect as a subcontractor.",
        points: [
          "Contractors must register, verify every subcontractor with HMRC, and deduct 20% (registered), 30% (unregistered) or 0% (gross payment status) from labour.",
          "Deductions apply to labour only — materials, plant hire and VAT are excluded if shown separately on the invoice.",
          "File the monthly CIS return by the 19th and pay HMRC by the 22nd (electronic); issue deduction statements to subbies.",
          "Limited company subcontractors offset CIS suffered against their PAYE/NI liabilities each month via the EPS.",
          "Gross payment status is worth applying for once you meet the turnover and compliance tests — it transforms cashflow.",
        ],
        teaser: [
          "The three deduction rates and how HMRC decides which applies to you",
          "What you can leave out of a CIS deduction — if your invoice is laid out correctly",
          "The monthly deadline that triggers £100 penalties the day you miss it",
        ],
        checklist: [
          "Confirm every subcontractor has been verified with HMRC before their first payment.",
          "Check that your invoices split labour from materials, plant and VAT.",
          "Put the 19th (return) and 22nd (payment) in the diary every month.",
          "If you're a limited company subcontractor, check CIS suffered is being offset via your EPS.",
        ],
      },
      {
        title: "VAT domestic reverse charge for construction",
        summary: "When the customer accounts for the VAT instead of you.",
        points: [
          "Generally applies to qualifying standard or reduced-rate construction services between UK VAT-registered businesses where the payment is reportable under CIS and no written end-user or intermediary-supplier notification has been given. Zero-rated work and supplies of workers by employment businesses are excluded.",
          "Do not add VAT to the amount payable. Show the reverse charge VAT amount or applicable rate, and include 'Reverse charge: customer to account for VAT to HMRC'.",
          "End users and qualifying intermediary suppliers must notify you in writing if they want normal VAT treatment instead of the reverse charge — retain their confirmation.",
          "Subcontractors may become VAT repayment traders — monthly VAT returns can help recover VAT sooner and improve cashflow.",
          "Xero and QuickBooks support construction reverse charge VAT codes — check that the correct codes are enabled and used on invoices and bills.",
        ],
        teaser: [
          "How to tell in seconds whether an invoice should carry VAT or the reverse charge",
          "The written notification that switches an invoice back to normal VAT",
          "Why monthly VAT returns can help subcontractors' cashflow",
        ],
        checklist: [
          "Check whether each customer is VAT registered, whether the payment is reportable under CIS, and whether the service qualifies.",
          "Obtain and retain written notification where a customer claims the end-user or intermediary-supplier exception.",
          "Check your invoice template shows the reverse charge VAT amount or rate and carries the reverse-charge wording.",
          "Ask whether monthly VAT returns would improve your cashflow as a repayment trader.",
        ],
      },
      {
        title: "Getting paid: retentions, applications & stage payments",
        summary: "Keeping cash coming in across long jobs.",
        points: [
          "Track retentions separately, recording the amount, release conditions and expected payment date — and diarise follow-up.",
          "An application for payment is not necessarily a VAT invoice — issuing a VAT invoice or receiving payment can trigger VAT accounting, so check the timing before billing.",
          "Agree clear stage-payment terms, due dates and final payment dates, then submit applications or invoices promptly in line with the contract.",
          "Price jobs with a contingency and track actual costs against the quote while the job is live.",
          "Keep a work-in-progress schedule showing costs, progress, amounts billed and expected costs to complete — this helps your accountant recognise revenue and profit in the correct period.",
        ],
        teaser: [
          "How to keep track of retentions so they're never forgotten",
          "Application for payment vs VAT invoice — why the difference matters to HMRC",
          "The simple schedule that helps profit land in the right year",
        ],
        checklist: [
          "List every retention you're owed, with the amount, release conditions and expected payment date.",
          "Review your contract templates for clear stage-payment milestones, application deadlines, due dates and final payment dates.",
          "Compare actual costs against the quote on your current live jobs.",
          "Set up a simple work-in-progress schedule for jobs spanning your year-end.",
        ],
      },
      {
        title: "Sole trader or limited company for trades?",
        summary: "The practical differences for people working under CIS.",
        points: [
          "Sole traders claim credit for CIS deductions through Self Assessment — if deductions exceed the final liability, a refund may be due after the tax return is submitted.",
          "Limited companies can offset CIS deductions suffered against PAYE scheme liabilities during the tax year, which may improve cashflow. Unused deductions are generally reclaimed after year-end.",
          "Construction risks make personal liability worth considering: sole traders are personally responsible for business debts, while a limited company generally offers protection — subject to personal guarantees and other exceptions.",
          "Higher basic and higher-rate dividend tax from April 2026 can reduce the tax advantage of incorporation — compare your profits, drawings, pension plans and running costs before deciding.",
          "Tax relief is available for qualifying vans, tools and plant under either structure, but the method depends on your accounting basis, the asset and how it is financed — check before buying.",
        ],
        teaser: [
          "How CIS deductions are recovered differently by sole traders and companies",
          "What limited liability really protects when a job goes wrong",
          "How the April 2026 dividend tax changes affect the comparison",
        ],
        checklist: [
          "Note your profit for the last 12 months and your plans for the next two years.",
          "List the personal assets you'd want protected if a job went wrong.",
          "Check how CIS deductions are recovered under your current structure, and when.",
          "Compare ongoing accounting, payroll and Companies House requirements alongside any potential tax savings.",
          "Talk to us before any big van, tool or plant purchase so the relief and timing work for you.",
        ],
      },
    ],
    relatedPosts: ["autumn-budget-2025-what-it-really-means-for-you-your-business", "navigating-employer-responsibilities-in-2024-a-guide-for-small-businesses"],
  },
];

export const getNiche = (slug) => NICHES.find((n) => n.slug === slug);
